import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { TagResponse } from '../types/Product';
import { fetchTagsWithProductCount, createTag, updateTag, deleteTag } from '../services/tagsList';

interface TagsContextType {
  tags: TagResponse[];
  loading: boolean;
  error: string | null;
  refreshTags: () => Promise<void>;
  createNewTag: (name: string) => Promise<void>;
  updateExistingTag: (id: number, name: string) => Promise<void>;
  deleteExistingTag: (id: number) => Promise<void>;
}

const TagsContext = createContext<TagsContextType | undefined>(undefined);

interface TagsProviderProps {
  children: ReactNode;
}

export const TagsProvider: React.FC<TagsProviderProps> = ({ children }) => {
  const [tags, setTags] = useState<TagResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const loadTags = async () => {
    try {
      setLoading(true);
      setError(null);
      const fetchedTags = await fetchTagsWithProductCount();
      console.log('Fetched tags:', fetchedTags);
      setTags(fetchedTags || []);
    } catch (err) {
      console.error('Error loading tags:', err);
      setError(err instanceof Error ? err.message : 'Failed to load tags');
    } finally {
      setLoading(false);
    }
  };

  const refreshTags = async () => {
    await loadTags();
  };

  const createNewTag = async (name: string) => {
    try {
      setError(null);
      const newTag = await createTag(name);
      setTags(prevTags => [...prevTags, newTag]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create tag');
      throw err;
    }
  };

  const updateExistingTag = async (id: number, name: string) => {
    try {
      setError(null);
      const updatedTag = await updateTag(id, name);
      setTags(prevTags => 
        prevTags.map(tag => tag.id === id ? updatedTag : tag)
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update tag');
      throw err;
    }
  };

  const deleteExistingTag = async (id: number) => {
    try {
      setError(null);
      const deleteResponse = await deleteTag(id);
      console.log('Tag deleted:', deleteResponse);
      setTags(prevTags => prevTags.filter(tag => tag.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete tag');
      throw err;
    }
  };

  useEffect(() => {
    loadTags();
  }, []);

  const value = {
    tags,
    loading,
    error,
    refreshTags,
    createNewTag,
    updateExistingTag,
    deleteExistingTag,
  };

  return (
    <TagsContext.Provider value={value}>
      {children}
    </TagsContext.Provider>
  );
};

export const useTags = (): TagsContextType => {
  const context = useContext(TagsContext);
  if (context === undefined) {
    throw new Error('useTags must be used within a TagsProvider');
  }
  return context;
};